import { Navigate } from "react-router-dom";
import { Spinner } from "../ui/Spinner";
import { usePlanFeature } from "../../hooks/usePlanFeature";

interface RequireFeatureRouteProps {
  /** Key in the subscription's `features` JSONB, e.g. "accounting". */
  feature: string;
  children: React.ReactNode;
  /** Where to send users whose plan lacks the feature. Defaults to the dashboard. */
  redirectTo?: string;
}

/**
 * Route-level plan-feature guard: renders children when the flag is on,
 * otherwise redirects. Use alongside the Sidebar filter so a hidden nav item is
 * not still reachable by typing the URL. UX only — the API stays open, because
 * sync keeps posting to the ledger whether or not the screens are visible.
 */
export function RequireFeatureRoute({
  feature,
  children,
  redirectTo = "/dashboard",
}: RequireFeatureRouteProps) {
  const { enabled, isLoading } = usePlanFeature(feature);

  // Don't bounce a flagged business off its own page while the status loads.
  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spinner size="lg" className="text-brand" />
      </div>
    );
  }

  if (!enabled) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
